import React, { useState, useEffect, useRef } from "react";
import {
  Layout,
  Menu,
  Button,
  Modal,
  Form,
  Input,
  notification,
  Select,
  List,
  Dropdown,
  Spin,
} from "antd";
import { Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  HomeOutlined,
  FileOutlined,
  FileSearchOutlined,
  ReadOutlined,
  ShopOutlined,
  LogoutOutlined,
  UserOutlined,
  SettingOutlined,
  PlusOutlined,
  MenuOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
  BellOutlined,
  RobotOutlined,
  FilePdfOutlined,
  DollarOutlined,
  PlusSquareOutlined,
  FolderOutlined,
  SafetyCertificateOutlined,
  ToolOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import {
  createProject,
  fetchInvitations,
  acceptInvitation,
} from "../services/projectsAPI";
import { fetchCurrentUser } from "../services/authAPI";
import { convertRole } from "../utils/textUtils";
import { getChangeOrderLabel } from "../utils/projectUtils";
import UpgradeWarningModal from "./UpgradeWarningModal";
import HelpChatWidget from "./help/HelpChatWidget";
import { Header } from "antd/es/layout/layout";
const { Sider, Content } = Layout;

const ProtectedLayout = () => {
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isInvitesModalVisible, setIsInvitesModalVisible] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [acceptingInvitationId, setAcceptingInvitationId] = useState(null);
  const {
    currentUser,
    isAuthenticated,
    logout,
    selectedProject,
    setSelectedProject,
    setCurrentUser,
    magicLoginActive,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [isUpgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const isMagicLinkLogin = localStorage.getItem("login_type") === "magic_login";
  const allowedMagicRoutes = ["/submittals", "/rfis", "/tasks"];
  const warningShownRef = useRef(false);

  const modalSelection = async () => {
    if (isAuthenticated && currentUser) {
      const invites = await fetchInvitations();
      if (currentUser.projects.length === 0 && invites.length === 0) {
        setIsCreateModalVisible(true);
      } else if (invites.length > 0) {
        setIsInvitesModalVisible(true);
      }
    }
  };

  useEffect(() => {
    if (
      isAuthenticated &&
      (isMagicLinkLogin || magicLoginActive) &&
      !warningShownRef.current
    ) {
      const isAllowed = allowedMagicRoutes.some((path) =>
        location.pathname.startsWith(path),
      );
      if (!isAllowed) {
        warningShownRef.current = true;
        notification.warning({
          message: "Access Restricted",
          description:
            "Your access is limited when using mail login link. Please log in normally for full access.",
        });
        logout();
        navigate("/login");
      }
    }
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    if (isAuthenticated) {
      modalSelection();
    }
  }, [isAuthenticated, currentUser]);

  useEffect(() => {
    if (isAuthenticated && currentUser) {
      setInvitationsLoading(true);
      fetchInvitations()
        .then(setInvitations)
        .finally(() => setInvitationsLoading(false));
    }
  }, [isAuthenticated, currentUser]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const updateUser = async () => {
    const user = await fetchCurrentUser();
    setCurrentUser(user);
    localStorage.setItem("user", JSON.stringify(user));
    window.location.reload();
  };

  const handleCreateProjectClick = () => {
    if (
      currentUser.projects.length > 0 &&
      currentUser.remaining_premium_project <= 0
    ) {
      setUpgradeModalVisible(true);
    } else {
      setIsCreateModalVisible(true);
    }
  };

  const createProjectMutation = useMutation({
    mutationFn: createProject,
    onSuccess: async (newProject) => {
      notification.success({ message: "Project created successfully" });
      const updatedUser = await fetchCurrentUser();
      setCurrentUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Find and select the newly created project
      const createdProject = updatedUser.projects.find(
        (p) => p.project_id === newProject._id,
      );
      if (createdProject) {
        setSelectedProject(createdProject);
      }

      setIsCreateModalVisible(false);
      navigate("/");
    },
    onError: (error) => {
      if (error.response.status === 402) {
        setUpgradeModalVisible(true);
      }
      notification.error({
        message: "Failed to create project",
        description: error.response.data.detail.message,
      });
    },
  });

  const acceptInvitationMutation = useMutation({
    mutationFn: acceptInvitation,
    onMutate: (invitationId) => {
      setAcceptingInvitationId(invitationId);
    },
    onSuccess: async () => {
      notification.success({ message: "Invitation accepted successfully" });
      try {
        await updateUser();
      } catch (e) {
        setAcceptingInvitationId(null);
        notification.error({
          message: "Invitation accepted",
          description:
            e?.message ||
            "Your session could not be refreshed. Try reloading the page.",
        });
        setIsInvitesModalVisible(false);
      }
    },
    onError: (error) => {
      setAcceptingInvitationId(null);
      notification.error({
        message: "Failed to accept invitation",
        description: error.message,
      });
    },
  });

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const restrictedPaths = ["/submittals", "/rfis", "/tasks"];
  if (
    currentUser &&
    currentUser.projects.length === 0 &&
    restrictedPaths.includes(location.pathname)
  ) {
    return <Navigate to="/" replace />;
  }

  const menuItems = [
    <>
      <Menu.Item key="0">
        <Select
          showSearch
          placeholder="Select Project"
          optionFilterProp="children"
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          value={selectedProject?.project_id}
          onChange={(value) => {
            const project = currentUser.projects.find(
              (p) => p.project_id === value,
            );
            setSelectedProject(project);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="w-full"
          dropdownStyle={{
            maxWidth: "300px",
          }}
          optionLabelProp="label"
        >
          {currentUser.projects
            .filter(
              (project) =>
                !project.archived ||
                project.project_id === selectedProject?.project_id,
            )
            .map((project) => (
              <Select.Option
                key={project.project_id}
                value={project.project_id}
                label={project.project_name}
                style={{
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  lineHeight: "1.4",
                }}
              >
                <div
                  style={{
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.4",
                    padding: "4px 0",
                  }}
                >
                  {project.project_name}
                </div>
              </Select.Option>
            ))}
        </Select>
      </Menu.Item>
      <Menu.Item
        key="invitations"
        icon={<BellOutlined />}
        onClick={() => setIsInvitesModalVisible(true)}
      >
        Project Invites
      </Menu.Item>
      <Menu.Item key="/" icon={<HomeOutlined />} onClick={() => navigate("/")}>
        Home
      </Menu.Item>
    </>,
    currentUser.projects.length > 0 && (
      <>
        <Menu.Item
          key="/drawing-folders"
          icon={<FilePdfOutlined />}
          onClick={() => navigate("/drawing-folders")}
        >
          Drawings
        </Menu.Item>
        <Menu.Item
          key="/documents"
          icon={<FolderOutlined />}
          onClick={() => navigate("/documents")}
        >
          Documents
        </Menu.Item>
        <Menu.Item
          key="/submittals"
          icon={<FileOutlined />}
          onClick={() => navigate("/submittals")}
        >
          Submittals
        </Menu.Item>
        {selectedProject?.use_custom_terminology && (
          <Menu.Item
            key="/shop-drawings"
            icon={<FilePdfOutlined />}
            onClick={() => navigate("/shop-drawings")}
          >
            Shop Drawings
          </Menu.Item>
        )}
        <Menu.Item
          key="/rfis"
          icon={<FileSearchOutlined />}
          onClick={() => navigate("/rfis")}
        >
          RFIs
        </Menu.Item>
        <Menu.Item
          key="/tasks"
          icon={<CheckSquareOutlined />}
          onClick={() => navigate("/tasks")}
        >
          Tasks
        </Menu.Item>
        {(selectedProject?.role === "owner" ||
          selectedProject?.role === "project_manager" ||
          selectedProject?.role === "field") && (
          <Menu.Item
            key="/daily-reports"
            icon={<CalendarOutlined />}
            onClick={() => navigate("/daily-reports")}
          >
            Daily Reports
          </Menu.Item>
        )}
        <Menu.SubMenu
          key="project-more"
          icon={<PlusSquareOutlined />}
          title="More"
        >
          <Menu.Item
            key="/punch-lists"
            icon={<CheckCircleOutlined />}
            onClick={() => navigate("/punch-lists")}
          >
            Punch Lists
          </Menu.Item>
          <Menu.Item
            key="/change-orders"
            icon={<PlusSquareOutlined />}
            onClick={() => navigate("/change-orders")}
          >
            {getChangeOrderLabel(selectedProject, true)}
          </Menu.Item>
          {(selectedProject?.role === "owner" ||
            selectedProject?.role === "project_manager") && (
            <Menu.Item
              key="/contracts"
              icon={<DollarOutlined />}
              onClick={() => navigate("/contracts")}
            >
              Contracts
            </Menu.Item>
          )}
          {(selectedProject?.role === "owner" ||
            selectedProject?.role === "project_manager" ||
            selectedProject?.role === "field") && (
            <Menu.Item
              key="/schedule"
              icon={<CalendarOutlined />}
              onClick={() => navigate("/schedule")}
            >
              Schedule
            </Menu.Item>
          )}
          {(selectedProject?.role === "owner" ||
            selectedProject?.role === "project_manager" ||
            selectedProject?.role === "field") && (
            <Menu.Item
              key="/safety"
              icon={<SafetyCertificateOutlined />}
              onClick={() => navigate("/safety")}
            >
              Safety
            </Menu.Item>
          )}
          {(selectedProject?.role === "owner" ||
            selectedProject?.role === "project_manager") && (
            <Menu.Item
              key="/workers"
              icon={<RobotOutlined />}
              onClick={() => navigate("/workers")}
            >
              AI Workers
            </Menu.Item>
          )}
        </Menu.SubMenu>
      </>
    ),
    (selectedProject?.role === "owner" ||
      selectedProject?.role === "project_manager") && (
      <>
        <Menu.SubMenu
          key="project-setup"
          icon={<ToolOutlined />}
          title="Project Setup"
        >
          <Menu.Item
            key="/spec-sections"
            icon={<ReadOutlined />}
            onClick={() => navigate("/spec-sections")}
          >
            Spec Sections
          </Menu.Item>
          <Menu.Item
            key="/vendors"
            icon={<ShopOutlined />}
            onClick={() => navigate("/vendors")}
          >
            Vendors
          </Menu.Item>
          <Menu.Item
            key="/users"
            icon={<UserOutlined />}
            onClick={() => navigate("/users")}
          >
            Users
          </Menu.Item>
        </Menu.SubMenu>
      </>
    ),
    <Menu.Item
      key="/settings"
      icon={<SettingOutlined />}
      onClick={() => navigate("/settings")}
    >
      Settings
    </Menu.Item>,
  ];

  const mobileMenu = (
    <Menu
      selectedKeys={[location.pathname]}
      defaultSelectedKeys={[location.pathname]}
    >
      {menuItems}
    </Menu>
  );

  const handleCreateModalClose = () => {
    form.resetFields();
    setIsCreateModalVisible(false);
  };

  const handleInvitesModalClose = () => {
    if (acceptingInvitationId) return;
    setIsInvitesModalVisible(false);
  };

  return (
    <Layout hasSider className="max-h-screen">
      <Sider
        breakpoint="lg"
        collapsedWidth="0"
        trigger={null}
        className="fixed left-0 top-0 bottom-0 h-screen"
      >
        {/* Sidebar content wrapper with flexbox */}
        <div className="flex flex-col h-full">
          {/* Logo section */}
          <div className="logo flex items-center justify-center h-16 text-white text-2xl font-bold flex-shrink-0">
            <span>SubmittalLink</span>
          </div>

          {/* Menu section - takes remaining space and scrolls */}
          <div className="flex-1 overflow-auto sidebar-scrollable">
            <Menu
              theme="dark"
              mode="inline"
              defaultSelectedKeys={["1"]}
              selectedKeys={[location.pathname]}
            >
              {menuItems}
            </Menu>
          </div>

          {/* Logout button - always at bottom */}
          <div className="flex-shrink-0 p-4">
            <Button
              type="primary"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              className="flex items-center justify-center"
              block
            >
              Logout
            </Button>
          </div>
        </div>
      </Sider>
      <Layout
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Header className="lg:hidden px-6 flex justify-between items-center">
          <Dropdown overlay={mobileMenu} trigger={["click"]}>
            <Button type="primary" icon={<MenuOutlined />} />
          </Dropdown>
          <Button
            className="!w-24"
            type="primary"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            block
          >
            Logout
          </Button>
        </Header>
        <Content
          className="m-4 p-4 bg-white rounded-2xl overflow-hidden"
          style={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            className="help-chat-scroll-area overflow-auto pb-12"
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Outlet />
          </div>
        </Content>
      </Layout>

      {/* Create Project Modal */}
      <Modal
        title="Create New Project"
        open={isCreateModalVisible}
        footer={null}
        onCancel={handleCreateModalClose}
        centered
      >
        <Form
          form={form}
          onFinish={(values) => createProjectMutation.mutate(values)}
          layout="vertical"
        >
          <Form.Item
            label="Project Name"
            name="name"
            rules={[
              { required: true, message: "Please input the project name!" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={createProjectMutation.isPending}
            >
              Create Project
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Project Invites Modal */}
      <Modal
        title="Project Invitations"
        open={isInvitesModalVisible}
        footer={null}
        onCancel={handleInvitesModalClose}
        closable={!acceptingInvitationId}
        maskClosable={!acceptingInvitationId}
        centered
      >
        {invitationsLoading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <Spin size="large" />
            <span className="text-gray-500">Loading invitations…</span>
          </div>
        ) : invitations.length === 0 ? (
          <div className="text-center py-4">No pending invitations</div>
        ) : (
          <List
            dataSource={invitations}
            renderItem={(invitation) => (
              <List.Item
                actions={[
                  <Button
                    key="accept"
                    type="primary"
                    loading={acceptingInvitationId === invitation._id}
                    disabled={acceptingInvitationId !== null}
                    onClick={() =>
                      acceptInvitationMutation.mutate(invitation._id)
                    }
                  >
                    Accept
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={`Invitation to: ${invitation.project_name}`}
                  description={`Role: ${convertRole(invitation.role)} ${
                    invitation.vendor_name ? `(${invitation.vendor_name})` : ""
                  }`}
                />
              </List.Item>
            )}
          />
        )}
      </Modal>

      <UpgradeWarningModal
        visible={isUpgradeModalVisible}
        onClose={() => setUpgradeModalVisible(false)}
        text="You need to upgrade your membership to create more than one project."
        mode="create"
      />

      <HelpChatWidget />
    </Layout>
  );
};

export default ProtectedLayout;
